'use client';

import { AuthUser, getCurrentUser } from "@aws-amplify/auth";
import { GA_VALUES } from "@packages/constants/ga.const";
import { DataLayerGA4AttrType } from "@packages/lib/types/datalayerGA";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import { clickstreamForClicks, getCookieValue, isWhatuni } from "@packages/lib/utlils/commonFunction";
import { GA4DataLayer, GA4DataLayerFn } from "@packages/lib/utlils/helper-function";
import { addRemoveFavourites, getUserFavourites } from "@packages/lib/utlils/userfavourite";
import { ClickstreamConstants, GAConstants } from "@packages/shared-components/services/constants";
import { useEffect, useState } from "react";
import { createContext } from 'react';

type ContentType = 'INSTITUTION' | 'COURSE';

const PENDING_FAV = "pendingFavourite";

interface Favourite {
    favId: string;
    fav_id?: string;
    favType: string;
    favDate?: string;
    final_choice_id?: string | null;
    choice_position?: number | null;
}

interface FavouriteContext {
    favourites: number[],
    setFavourites?: Function,
    favourite?: (
        contentId: number,
        contentName: string,
        contentType: 'INSTITUTION' | 'COURSE') => Promise<{ status: string }>;
}

export const FavouriteContext = createContext<FavouriteContext>({ favourites: [] });

const FavFunctionalityWrapper = ({ children, commonGAAttrs, cSCommonData,commonOldGAAttrs }: any) => {
    const [favourites, setFavourites] = useState<number[]>([]);

    useEffect(() => {
        async function getFavouritesList() {
            try {
                let user: AuthUser | undefined | boolean = undefined;
                if (isWhatuni())
                    user = await getCurrentUser();
                else {
                    user = !!getCookieValue('pgs_userid');
                    if (!user) throw new Error('Unauthorized user')
                }
                if (user && typeof window !== "undefined") {
                    const favList: Favourite[] = (await getUserFavourites())?.favourites;
                    if (favList?.length) {
                        let formattedList: number[] = favList?.map((fav: any) => +(fav?.favId || fav?.fav_id));
                        setFavourites(() => [...formattedList]);
                    }
                }
            } catch (error) {
                setFavourites([]);
            }
        }

        async function resumeFavouriteAction() {
            const content = localStorage.getItem(PENDING_FAV);
            if (content) {
                localStorage.removeItem(PENDING_FAV);
                const { contentId, contentName, contentType, clickStreamData } = { ...JSON.parse(content) };
                favClickStreamLog(contentType, clickStreamData);
                await favourite(contentId, contentName, contentType, true);
            }
        }

        getFavouritesList();
        resumeFavouriteAction();
    }, []);

    const favourite = async (contentId: number,
        contentName: string,
        contentType: ContentType,
        isResume?: boolean,statsGAData?:any,
        clickStreamData?: any,statsOldGAData?:DataLayerGA4AttrType): Promise<{ status: string }> => {
        let returnMsg: { status: string } = { status: '' };
        try {
            if (isWhatuni())
                await getCurrentUser();
            else {
                const pgsUserToken = getCookieValue('pgs_userid')
                if (!pgsUserToken)
                    throw new Error('Unauthorized user');
            }
        } catch (error) {
            console.log(error, "fav error");
            if (isResume) {
                localStorage.removeItem(PENDING_FAV);
            } else {
                localStorage.setItem(PENDING_FAV, JSON.stringify({ contentType, contentId, contentName, clickStreamData }));
                window.location.href = "/register/";
            }
            return returnMsg;
        }
        const isAdd = !favourites?.includes(+contentId);
        const payload = { contentType, contentId, contentName, inputFlag: isAdd };
        try {
            updateAction(isAdd, payload);
            const data = await addRemoveFavourites([payload]);
            switch (data?.message?.toLowerCase()) {
                case 'added course':
                case 'added institution':
                    returnMsg.status = 'added';
                    favGALog(isAdd, statsGAData,statsOldGAData);
                    favClickStreamLog(payload.contentType, clickStreamData);
                    break;
                case 'removed course':
                case 'removed institution':
                    returnMsg.status = 'removed';
                    favGALog(isAdd, statsGAData,statsOldGAData);
                    break;
                case 'limit exceeded':
                    returnMsg.status = 'limit exceeded';
                    updateAction(!isAdd, payload);
                    break;
                default:
                    updateAction(!isAdd, payload);
            }
        } catch (error) {
            console.log('error', error)
            updateAction(!isAdd, payload);
        }
        return returnMsg;
    }

    function updateAction(isAdd: boolean, payload: any) {
        if (isAdd)
            setFavourites((prev: any[]) => {
                return [...prev, payload?.contentId];
            });
        else
            setFavourites((prev: any[]) => {
                return prev?.filter(ele => ele !== payload?.contentId);
            });
    }

    const favGALog = (isAdd: boolean, statsGAData?: any, statsOldGAData?: DataLayerGA4AttrType) => {
        GA4DataLayer({
            event_name: isAdd ? GAConstants.favourite : GAConstants.unfavourite,
            event: GAConstants.track_kpi,
            page_name: localStorage?.getItem("gaPageName") || "NA",...statsGAData,
            ...commonGAAttrs
        });
        GA4DataLayerFn({
            eventName: isAdd ? GAConstants.favourite : GAConstants.unfavourite,
            event: "ga_events",
            page_name: localStorage?.getItem("gaPageName") || "NA",...statsOldGAData,
            ...commonOldGAAttrs
        });
    }

    const favClickStreamLog = (type: ContentType, clickStreamData: any = {}) => {
        let csData: ClickstreamInterface = {} as ClickstreamInterface;
        csData.actionType = ClickstreamConstants.clickAction;
        csData.eventType = type === "INSTITUTION" ? "InstitutionBasketDetailViewed" : "CourseBasketDetailViewed";
        const mergedClickStreamData = { ...cSCommonData, ...clickStreamData };
        clickstreamForClicks(mergedClickStreamData, csData, commonGAAttrs);
    }

    return <FavouriteContext.Provider value={{ favourites, setFavourites, favourite }}>
        {children}
    </FavouriteContext.Provider>;
}

export default FavFunctionalityWrapper;